import { useEffect, useMemo, useRef, useState } from "react";
import summaryCommonStore from "@/store/summaryCommon.store";
import Button from "@/component/Button";
import ArrowRight from "@/assets/common/ic_arrow_double_right.svg?react";
import summarySrcStore from "@/store/summarySrc.store";
import { message } from "antd";
import usePermission from "@/sections/Summary/usePermission";
import recordStore from "@/store/record.store";
import { saveDoc } from "@/services/api_record";
import { formatDocTc } from "@/utils/formatDocTc";
import fridayReporter from "@/utils/friday";
import EditIcon from "@/assets/summary/edit.svg?react";
import DocComplete from "./DocComplete";
import SrcSkeleton from "./SrcSkeleton";
import "./index.css";
import OriginTextAnchor from "@/component/OriginTextAnchor";

let timer: NodeJS.Timeout;

interface RecordContentProps {
  isExpan: boolean;
  isSummaryExpand: boolean;
  isSummaryHidden: boolean;
  setIsExpan: (isExpan: boolean) => void;
}

const RecordContent = ({ isExpan, isSummaryExpand, isSummaryHidden, setIsExpan }: RecordContentProps) => {
  const detailInfo = summarySrcStore((state) => state.detailInfo);
  const recordEditing = summarySrcStore((state) => state.recordEditing);
  const setRecordEditing = summarySrcStore((state) => state.setRecordEditing);
  const updateDetailInfo = summarySrcStore((state) => state.updateDetailInfo);
  const setIsFollowMode = summaryCommonStore((state) => state.setIsFollowMode);
  const setIsScrolled = summaryCommonStore((state) => state.setIsScrolled);
  const showBackTrace = summaryCommonStore((state) => state.showBackTrace);
  const records = recordStore((state) => state.records);
  const tempRecords = recordStore((state) => state.tempRecords);
  const setRecords = recordStore((state) => state.setRecords);
  const setTempRecords = recordStore((state) => state.setTempRecords);
  const isRecordContentFull = isExpan && !isSummaryExpand;
  const [moduleWidth, setModuleWidth] = useState(0);
  const moduleRef = useRef(null);

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const { isOwner, allowBackUp, showEdit } = usePermission();

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let scrollContainer = scrollContainerRef.current as any;
    const onWheel = () => {
        setIsScrolled(true);
        setIsFollowMode(true);
        timer && clearTimeout(timer);
        timer = setTimeout(() => {
          setIsFollowMode(false);
        }, 100);
    };
    scrollContainer.addEventListener("wheel", onWheel);
    return () => {
      scrollContainer.removeEventListener("wheel", onWheel);
      scrollContainer = null;
    };
  }, [setIsScrolled, setIsFollowMode]);

  const handleEditSrcContent = () => {
    fridayReporter.pushData({
      eventCode: "SummaryPage_Text_Edit",
    });
    if (!isOwner && allowBackUp) {
      message.warning("创建副本后可使用该功能");
      return;
    }
    setTempRecords(records);
    setRecordEditing(!recordEditing);
    summaryCommonStore.getState().setShowBackTrace(false);
  };

  const handleCancel = () => {
    setTempRecords([]);
    setRecords(tempRecords);
    setRecordEditing(false);
  };

  const handleSaveEdit = async () => {
    try {
      setRecordEditing(false);
      const formatRecords = formatDocTc([...recordStore.getState().records]);
      await saveDoc({
        streamName: detailInfo?.streamName || "",
        srcDoc: {
          pg: formatRecords,
        },
      });
      message.success("修改成功");
      updateDetailInfo();
    } catch (error) {
      console.error("修改原文失败", error);
    }
  };

  const renderOperation = useMemo(() => {
    if (recordEditing) {
      return (
        <>
          <Button
            onClick={handleCancel}
            className="mr-2 h-8 !p-y-2 !p-x-4 flex items-center"
          >
            取消
          </Button>
          <Button
            type="primary"
            onClick={handleSaveEdit}
            className="h-8 !p-y-2 !p-x-4 flex items-center"
          >
            完成
          </Button>
        </>
      );
    }

    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordEditing]);

  useEffect(() => {
    return () => {
      timer && clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    const moduleElement = moduleRef.current;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const updateWidth = (entries: any) => {
      for (const entry of entries) {
        setModuleWidth(entry.contentRect.width);
      }
    };
    const resizeObserver = new ResizeObserver(updateWidth);
    if (moduleElement) {
      resizeObserver.observe(moduleElement);
    }
    return () => {
      if (moduleElement) {
        resizeObserver.unobserve(moduleElement);
      }
    };
  }, []);

  const getRight = () => {
    if(isRecordContentFull){
      return recordEditing ? 'right-50.5' : 'right-26.5';
    }
    if(moduleWidth >= 492){
      return recordEditing ? 'right-40.5' : 'right-16.5';
    }
    return 'right-4.5';
  }
  

  return (
    <>
      <div ref={moduleRef} className="content-box min-w-75 flex h-full p-2 pb-2.5">
        <div
          className={`px-1.5 flex h-full min-w-40% flex-1 rounded-2 rounded-r-0 rounded bg-#F4F6F9 flex-col relative pt-3 overflow-y-scroll transition-padding duration-100 items-center`}
        >
          <div
            className={`relative w-full max-w-200 min-h-8 flex items-center justify-between text-4.5 font-600 text-mx-secondary px-4.5`}
          >
            原文
            <div className="flex items-center">
              {!recordEditing && !!records.length && showEdit && (
                <div
                  className="toolbox items-center cursor-pointer"
                  onClick={handleEditSrcContent}
                >
                  <EditIcon className="size-4.5" />
                </div>
              )}
              {renderOperation}
              {(!showBackTrace || isRecordContentFull) && !isSummaryHidden && <ArrowRight
                className="ml-6 size-4 cursor-pointer"
                onClick={() => {
                  setIsExpan(false);
                  fridayReporter.pushData({
                    eventCode: "SummaryPage_Text_Fold",
                  });
                }}
              />}
            </div>
          <div id="back-track-search" className={`back-track-search-bar ${getRight()}`}></div>
          </div>
          <div
            className="flex-1 w-full max-w-200 my-scrollbar mt-1.5 pt-2.5 pl-4.5 pb-2"
            ref={scrollContainerRef}
          >
            {detailInfo?.srcDoc && <DocComplete isExpan={isExpan} setIsExpan={setIsExpan} />}
            {!detailInfo?.completeDocFinish && (
              <SrcSkeleton className="max-w-200 mb-3" />
            )}
          </div>
          <OriginTextAnchor className="right-4 bottom-0" />
        </div>
      </div>
      <div
        className={`${
          isExpan ? "hidden" : "flex"
        } absolute right-0 top-5 bg-#F4F6F9 h-9 min-w-16 p-2.5 color-#626773 text-3 rounded-lb-5 rounded-lt-5 items-center cursor-pointer`}
        onClick={() => setIsExpan(true)}
      >
        <ArrowRight className="size-3.5 rotate--180" />
        <span className="ml-.5">原文</span>
      </div>
    </>
  );
};

export default RecordContent;
